import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { iaCrm } from '@modules/aawz-intelligence-v2/constants/URL'
import { ERRO_INESPERADO } from '@modules/aawz-intelligence-v2/api/guardas'

/**
 * Catálogo de atalhos de negócio do menu `/` — spec-001
 * (`project-home-ia-skills/preparacao-reuniao`, [T015], US-1) e spec-013.
 *
 * `GET /v2/api/ia-crm/atalhos` já existe e já filtra por papel e escritório
 * (`aawz_intelligence_v2/core/atalhos/catalogo.py::catalogo_para`, RBAC em
 * `limiares_office.py`) — esta classe só fala HTTP. **O front NUNCA refiltra**:
 * a lista que volta é exatamente o que o consultor pode executar.
 *
 * ★ Contrato REAL, verificado no código do backend (não no doc de plano) —
 * `resources/resource_atalhos.py` + `core/atalhos/catalogo.py::Atalho.
 * para_payload()` + `tests/test_api_atalhos.py` (asserts de valor, camelCase):
 *
 * ```json
 * {"status": 200, "message": "OK",
 *  "value": [{"id": "pre-reuniao", "label": "/pré-reunião",
 *             "description": "Briefing para a próxima reunião",
 *             "pedeArgumento": true, "placeholderArgumento": "nome do cliente"}]}
 * ```
 *
 * ⚠️ **Diverge do `contracts/api.md` §1 da spec-001** (que descreve `slug`/
 * `descricao`/`requer_argumento`/`tipo_argumento`, sem `id`/`description`/
 * `pedeArgumento`/`placeholderArgumento`) — o contrato foi escrito antes de o
 * catálogo real (spec-013, [T002]/[T013]) existir. Regra do CLAUDE.md raiz
 * ("quando doc diverge do código real, o código vence, reporte a
 * divergência"): esta classe segue o CONTRATO REAL. `id` é o `slug` que
 * `POST /chat` espera em `comando.slug`.
 *
 * ★ [merge release/ia-claude-code, 20/09/2026] DOIS métodos, e não um: a
 * spec-001 (chat imersivo) e a spec-013 (chat da plataforma) chegaram a este
 * arquivo pelos dois lados do merge, cada uma com a sua forma de retorno, e
 * as duas telas estão vivas. `listar()` devolve o ENVELOPE — é o que
 * `view-chat-imersivo-atalhos.js` usa para distinguir 403 de falha de rede e
 * mostrar o erro. `buscarCatalogo()` devolve o ARRAY — é o que
 * `view-chat.vue` usa, onde a seção "Atalhos" apenas some em qualquer falha.
 * Um é escrito sobre o outro para que exista UMA chamada HTTP e UMA regra de
 * erro; colapsar num método só quebraria uma das duas telas.
 */
class APIAtalhos extends APITemplate {
  /**
   * @returns {Promise<Object>} envelope `{ status, message, value }`. `value`
   *   é `[]` em QUALQUER falha — nunca atalho placeholder como se fosse do
   *   escritório (regressão conhecida, US-1 edge).
   */
  async listar() {
    const r = await con.get(iaCrm.atalhos)
    if (!r || r.status !== 200) {
      return this.buildErrorResponse({
        message: (r && r.message) || ERRO_INESPERADO,
        // `r.status` viaja intacto (403 do servidor continua 403 para quem
        // chamou) — só a AUSÊNCIA de resposta (falha de rede) cai no default
        // 500 do `buildErrorResponse`.
        status: r && r.status ? r.status : undefined,
        value: [],
      })
    }
    return this.buildSuccessResponse({ value: r.value || [] })
  }

  /**
   * @returns {Promise<Array>} `[{ id, label, description, pedeArgumento,
   *   placeholderArgumento }]` no vocabulário de `Atalho.para_payload()`.
   *   Falha (rede, `status !== 200`, corpo ausente) devolve `[]`: a seção
   *   "Atalhos" some, sem derrubar o menu inteiro.
   */
  async buscarCatalogo() {
    const r = await this.listar()
    return (r && r.value) || []
  }
}

// ★ [D-2, design verify 20/09] As DUAS declarações — e não
// `export { apiAtalhos, APIAtalhos }` numa só. A regra de singleton do
// `design-system:no-regression` casa `/export\s+\{\s*\w+\s*\}/`, que
// aceita UM símbolo por bloco: a forma antiga saía BLOCKED (rc=2) e o DoD
// do repo cobra o gate verde no escopo alterado. Esta é a forma do irmão
// canônico do mesmo diretório (`apiChat.js`), medida em PASS.
export const apiAtalhos = new APIAtalhos()
export { APIAtalhos }
